import { memo } from 'react';
import { ImageWithFallback } from '../figma/ImageWithFallback';

interface ConversationItemProps {
  conversation: any;
  otherParticipant: any;
  isOnline: boolean;
  isActive: boolean;
  hasUnread: boolean;
  onSelect: () => void;
  formatMessageTime: (timestamp: string) => string;
}

// Memoized conversation item - only re-renders when props actually change
export const ConversationItem = memo(({ 
  conversation, 
  otherParticipant, 
  isOnline, 
  isActive, 
  hasUnread,
  onSelect,
  formatMessageTime
}: ConversationItemProps) => {
  return (
    <div
      onClick={onSelect}
      className={`group flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
        isActive 
          ? 'bg-rose-50 border-l-4 border-rose-600' 
          : 'hover:bg-gray-50 border-l-4 border-transparent'
      }`}
    >
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 rounded-full overflow-hidden">
          {otherParticipant?.profile?.profilePhoto ? (
            <ImageWithFallback
              src={otherParticipant.profile.profilePhoto}
              alt={`${otherParticipant?.firstName} ${otherParticipant?.lastName}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-semibold">
              {otherParticipant?.firstName?.charAt(0)}{otherParticipant?.lastName?.charAt(0)}
            </div>
          )}
        </div>
        {isOnline && (
          <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-500 ring-2 ring-white" />
        )}
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex justify-between items-baseline mb-0.5">
          <p className={`truncate ${hasUnread ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>
            {otherParticipant?.firstName} {otherParticipant?.lastName?.charAt(0)}.
          </p>
          <p className={`text-xs flex-shrink-0 ml-2 ${isActive ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
            {conversation.lastMessage?.createdAt ? formatMessageTime(conversation.lastMessage.createdAt) : ''}
          </p>
        </div>
        <div className="flex justify-between items-center">
          <p className={`text-sm truncate ${hasUnread ? 'font-bold text-gray-900' : 'text-gray-600'}`}>
            {conversation.lastMessage?.content || 'Start a conversation'}
          </p>
          {hasUnread && (
            <span className="h-2 w-2 bg-rose-600 rounded-full flex-shrink-0 ml-2" />
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific values change
  return (
    prevProps.conversation._id === nextProps.conversation._id &&
    prevProps.conversation.lastMessage?.content === nextProps.conversation.lastMessage?.content &&
    prevProps.conversation.lastMessage?.createdAt === nextProps.conversation.lastMessage?.createdAt &&
    prevProps.conversation.unreadCount === nextProps.conversation.unreadCount &&
    prevProps.isOnline === nextProps.isOnline &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.hasUnread === nextProps.hasUnread &&
    prevProps.otherParticipant?._id === nextProps.otherParticipant?._id
  );
});

ConversationItem.displayName = 'ConversationItem';
